import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cn } from '@/utils/cn';
import { CampaignTypePickerModal } from '@/components/campaign-create/CampaignTypePickerModal';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { WorkspaceCampaignsSection } from '@/components/workspace/WorkspaceCampaignsSection';
import { WorkspacePortfoliosSection } from '@/components/workspace/WorkspacePortfoliosSection';
import { useWorkspaceCatalog } from '@/hooks/useWorkspaceCatalog';
import { useWorkspaceCoreReportMetrics } from '@/hooks/useWorkspaceReportMetrics';
import {
  buildEntityGraph,
  defaultWorkspaceColumnFilters,
  emptyCrossFilter,
  type WorkspaceColumnFilters,
} from '@/utils/entityCrossFilter';

const ADVERTISING_TABS = [
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'portfolios', label: 'Portfolios' },
] as const;

type AdvertisingTab = (typeof ADVERTISING_TABS)[number]['id'];

export function CampaignListPage() {
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = profileIdParam ? decodeURIComponent(profileIdParam) : '';
  const { range: rangeState } = useAppDateRange();
  const catalog = useWorkspaceCatalog(profileId);
  const [columnFilters, setColumnFilters] = useState<WorkspaceColumnFilters>(
    defaultWorkspaceColumnFilters
  );
  const [pendingCampaignKeys, setPendingCampaignKeys] = useState<string[]>([]);
  const [pendingPortfolioIds, setPendingPortfolioIds] = useState<string[]>([]);
  const [createCampaignPickerOpen, setCreateCampaignPickerOpen] = useState(false);
  const appliedFilter = useMemo(() => emptyCrossFilter(), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: AdvertisingTab =
    searchParams.get('tab') === 'portfolios' ? 'portfolios' : 'campaigns';
  // Mount Portfolios on first visit, then keep it mounted so its filters and page survive tab switches.
  const [portfoliosMounted, setPortfoliosMounted] = useState(activeTab === 'portfolios');
  useEffect(() => {
    if (activeTab === 'portfolios') setPortfoliosMounted(true);
  }, [activeTab]);

  const selectTab = (tab: AdvertisingTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'campaigns') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    setColumnFilters(defaultWorkspaceColumnFilters());
    setPendingCampaignKeys([]);
    setPendingPortfolioIds([]);
  }, [profileId]);

  const graph = useMemo(() => {
    if (!profileId || catalog.isLoading) return null;
    return buildEntityGraph({
      portfolios: catalog.portfolios,
      campaigns: catalog.campaigns,
      adGroups: catalog.adGroups,
      spKeywords: catalog.spKeywords,
      sbKeywords: catalog.sbKeywords,
    });
  }, [
    profileId,
    catalog.isLoading,
    catalog.portfolios,
    catalog.campaigns,
    catalog.adGroups,
    catalog.spKeywords,
    catalog.sbKeywords,
  ]);

  const reportMetrics = useWorkspaceCoreReportMetrics(profileId, rangeState, graph, null);
  const emptyPlacementMetrics = useMemo(
    () => new Map<string, Map<never, never>>(),
    []
  );

  const refreshMetrics = () => {
    catalog.refetchAll();
    reportMetrics.refetch();
  };

  if (!profileId) {
    return <p className="p-6 text-body-sm text-foreground-muted">Missing account profile.</p>;
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-3.5 px-6 pb-[18px] pt-1 font-sans">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Campaigns</h1>
        <p className="mt-0.5 text-body-md text-foreground-muted">
          Search and filter campaigns for this account.
        </p>
      </header>
      <div
        role="tablist"
        aria-label="Advertising section"
        className="flex gap-4 border-b border-border text-sm"
      >
        {ADVERTISING_TABS.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={`advertising-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`advertising-panel-${tab.id}`}
              className={cn(
                'border-b-2 px-1 pb-2',
                selected ? 'border-brand text-brand' : 'border-transparent text-foreground-muted'
              )}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div>
        <div
          id="advertising-panel-campaigns"
          role="tabpanel"
          aria-labelledby="advertising-tab-campaigns"
          hidden={activeTab !== 'campaigns'}
        >
          <WorkspaceCampaignsSection
            profileId={profileId}
            graph={graph}
            columnFilters={columnFilters}
            onColumnFiltersChange={(patch) =>
              setColumnFilters((prev) => ({ ...prev, ...patch }))
            }
            appliedFilter={appliedFilter}
            pendingCampaignKeys={pendingCampaignKeys}
            onChangePendingCampaignKeys={setPendingCampaignKeys}
            selection={{
              selectionCount: pendingCampaignKeys.length,
              hasPendingChanges: false,
              canApply: false,
            }}
            showApplyFilter={false}
            onDiscardSelection={() => setPendingCampaignKeys([])}
            hasActiveCrossFilter={false}
            previousByCampaignKey={reportMetrics.previous.byCampaignKey}
            placementByCampaign={emptyPlacementMetrics}
            previousPlacementByCampaign={emptyPlacementMetrics}
            deferredLoading={false}
            showPlacementColumns={false}
            requireReportActivity={false}
            onCatalogRefresh={() => catalog.refetchAll()}
            onRefresh={refreshMetrics}
            showTitle={false}
            toolbarAction={
              <button
                type="button"
                onClick={() => setCreateCampaignPickerOpen(true)}
                className="text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                New campaign
              </button>
            }
          />
        </div>
        {portfoliosMounted ? (
          <div
            id="advertising-panel-portfolios"
            role="tabpanel"
            aria-labelledby="advertising-tab-portfolios"
            hidden={activeTab !== 'portfolios'}
          >
            <WorkspacePortfoliosSection
              profileId={profileId}
              graph={graph}
              columnFilters={columnFilters}
              onColumnFiltersChange={(patch) =>
                setColumnFilters((prev) => ({ ...prev, ...patch }))
              }
              appliedFilter={appliedFilter}
              pendingPortfolioIds={pendingPortfolioIds}
              onChangePendingPortfolioIds={setPendingPortfolioIds}
              selection={{
                selectionCount: pendingPortfolioIds.length,
                hasPendingChanges: false,
                canApply: false,
              }}
              showApplyFilter={false}
              onDiscardSelection={() => setPendingPortfolioIds([])}
              hasActiveCrossFilter={false}
              previousMetricsMaps={reportMetrics.previous}
              showTitle={false}
            />
          </div>
        ) : null}
      </div>
      <CampaignTypePickerModal
        profileId={profileId}
        isOpen={createCampaignPickerOpen}
        onClose={() => setCreateCampaignPickerOpen(false)}
      />
    </div>
  );
}
